import type { ReactNode } from "react";
import { allowanceOf } from "./entitlements";
import type { Feature } from "./entitlements";
import { useCurrentUser, type CurrentUser } from "./use-current-user";

/**
 * Whether a feature has been released to this installation's users (EP-38).
 *
 * <p>Deliberately the opposite default from {@link useEntitlement}: a plan gate that does not know
 * the answer yet opens nothing, and an unknown entitlement key is *allowed* — a missing data row must
 * not take a working screen away. Release is the other way round. "Not in the plan" is an offer
 * (`UpsellGate` renders one); "not released" is not an offer at all, so the screen renders nothing,
 * and the only thing that hides it is the answer from the server saying so.
 *
 * <p>Which is also why this is not `enabled`: a roastery whose plan excludes couriers still sees the
 * tab with an upsell behind it. Withheld means the tab is not there.
 */
export function isReleased(user: CurrentUser | undefined, feature: Feature): boolean {
  return allowanceOf(user, feature).reason !== "UNRELEASED";
}

/** The same question from a component. Loading reads as released — see {@link isReleased}. */
export function useReleased(feature: Feature): boolean {
  const { user } = useCurrentUser();
  return isReleased(user, feature);
}

/**
 * Renders its children only while the feature is released. For buttons, cards and tabs inside screens
 * that otherwise stay — the one thing an existing feature has to add for EP-38, and nothing is
 * deleted to do it.
 */
export function Released({ feature, children }: { feature: Feature; children: ReactNode }) {
  return useReleased(feature) ? <>{children}</> : null;
}
